// @storybook-internal widget renderer catalogued through PlacedWidgetItem.
import React, { useMemo } from "react";
import "./widget-cards.css";

import type { T_ChatV8TableWidget } from "@lattice/core-lib/types/chat-v8";
import {
  formatTableCell,
  prepareTablePresentation,
} from "@lattice/shared-frontend/chat-v8";

export type T_TableWidgetCardProps = {
  data: T_ChatV8TableWidget;
  /**
   * Supplied when `data` already went through the shared presentation
   * pipeline (markdown-table segments arrive pre-prepared). Preparing twice
   * would re-apply row capping to an already-capped table. Mirrors the
   * mobile TableWidgetCard signature.
   */
  hiddenRowCount?: number;
};

/** Web mirror of the mobile TableWidgetCard on the shared presentation
 * module (row capping + copy text derivation). */
export function TableWidgetCard({
  data,
  hiddenRowCount,
}: T_TableWidgetCardProps) {
  const presentation = useMemo(
    () =>
      hiddenRowCount === undefined
        ? prepareTablePresentation(data)
        : { data, hiddenRowCount },
    [data, hiddenRowCount],
  );
  const table = presentation.data;

  return (
    <div className="cw-card">
      {table.title ? <p className="cw-title">{table.title}</p> : null}
      <table className="cw-table">
        <thead>
          <tr>
            {table.columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {table.columns.map((column) => (
                <td key={column.key}>{formatTableCell(row[column.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {presentation.hiddenRowCount > 0 ? (
        <p className="cw-muted">
          {presentation.hiddenRowCount} more row
          {presentation.hiddenRowCount === 1 ? "" : "s"} not shown.
        </p>
      ) : null}
    </div>
  );
}
